import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Product } from '../../types';
import { trackFacebookEvent } from '../../utils/facebookPixel';
import { getImageUrl } from '../../utils/imageUrl';
import {
  calculateProductPriceWithAttributes,
  normalizeOptions,
  formatExtraPriceBadge
} from '../../utils/attributeHelpers';

interface QuickOrderModalProps {
  product: Product;
  onClose: () => void;
}

const QuickOrderModal: React.FC<QuickOrderModalProps> = ({ product, onClose }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = i18n.language;

  // Selection state lives here — isolated from ProductCard
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedWeight, setSelectedWeight] = useState('');
  const [selectedCustom, setSelectedCustom] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [missingAttributes, setMissingAttributes] = useState<Record<string, boolean>>({});

  const primaryImage = getImageUrl(
    product.image ||
    product.primary_image ||
    product.images?.find(img => img.is_primary)?.image ||
    product.images?.[0]?.image
  );

  const hasColors = !!(product.attributes?.colors?.enabled && (product.attributes.colors.options || []).length > 0);
  const hasSizes = !!(product.attributes?.sizes?.enabled && (product.attributes.sizes.options || []).length > 0);
  const hasWeights = !!(product.attributes?.weights?.enabled && (product.attributes.weights.options || []).length > 0);
  const hasCustom = !!(product.attributes?.custom && product.attributes.custom.some(cg => cg.enabled && (cg.options || []).length > 0));

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Live dynamic price calculation based on chosen variants
  const currentDiscountPrice = product.discount_price ? calculateProductPriceWithAttributes(
    Number(product.discount_price),
    product.attributes,
    selectedColor,
    selectedSize,
    selectedWeight,
    selectedCustom
  ) : undefined;

  const currentRegularPrice = calculateProductPriceWithAttributes(
    Number(product.price),
    product.attributes,
    selectedColor,
    selectedSize,
    selectedWeight,
    selectedCustom
  );

  const currentFinalPrice = currentDiscountPrice || currentRegularPrice;

  const handleConfirm = () => {
    const missingColor = hasColors && !selectedColor;
    const missingSize = hasSizes && !selectedSize;
    const missingWeight = hasWeights && !selectedWeight;

    const missingCustom: Record<string, boolean> = {};
    let hasMissingCustom = false;
    (product.attributes?.custom || []).forEach(cg => {
      if (cg.enabled && (cg.options || []).length > 0) {
        const key = cg.id || cg.name;
        if (!selectedCustom[key]) {
          missingCustom[key] = true;
          hasMissingCustom = true;
        }
      }
    });

    if (missingColor || missingSize || missingWeight || hasMissingCustom) {
      setMissingAttributes({
        color: missingColor,
        size: missingSize,
        weight: missingWeight,
        ...missingCustom,
      });
      return;
    }

    const productName = currentLang === 'bn' ? product.name_bn : product.name_en;
    trackFacebookEvent('AddToCart', {
      content_id: product.id.toString(),
      content_name: productName,
      content_ids: [product.id.toString()],
      content_type: 'product',
      value: currentFinalPrice * quantity,
      currency: 'BDT',
    });

    const allSelectedAttributes: Record<string, string> = {
      ...(selectedColor ? { [currentLang === 'bn' ? 'রং' : 'Color']: selectedColor } : {}),
      ...(selectedSize ? { [currentLang === 'bn' ? 'সাইজ' : 'Size']: selectedSize } : {}),
      ...(selectedWeight ? { [currentLang === 'bn' ? 'ওজন' : 'Weight']: selectedWeight } : {}),
      ...selectedCustom,
    };

    const orderData = {
      product: {
        ...product,
        final_price: currentFinalPrice,
      },
      quantity: quantity,
      unit_price: currentFinalPrice,
      selected_color: selectedColor,
      selected_size: selectedSize,
      selected_weight: selectedWeight,
      selected_attributes: allSelectedAttributes,
    };

    sessionStorage.setItem('directOrder', JSON.stringify(orderData));
    onClose();
    navigate('/checkout');
  };

  const modal = (
    <div className="quick-order-overlay" onClick={onClose}>
      <div className="quick-order-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="qo-modal-header">
          <span className="qo-modal-title">
            {currentLang === 'bn' ? 'অর্ডার করুন' : 'Place Order'}
          </span>
          <button className="qo-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Product brief */}
        <div className="qo-modal-product">
          <img
            src={primaryImage}
            alt={currentLang === 'bn' ? product.name_bn : product.name_en}
            className="qo-modal-thumb"
          />
          <div className="qo-modal-product-info">
            <p className="qo-modal-product-name">
              {currentLang === 'bn' ? product.name_bn : product.name_en}
            </p>
            <p className="qo-modal-product-price">
              {currentDiscountPrice
                ? <>
                    <span className="qo-price">৳{currentDiscountPrice.toLocaleString()}</span>
                    <span className="qo-original-price">৳{currentRegularPrice.toLocaleString()}</span>
                  </>
                : <span className="qo-price">৳{currentRegularPrice.toLocaleString()}</span>
              }
            </p>
          </div>
        </div>

        {/* Scrollable attribute area */}
        {(hasColors || hasSizes || hasWeights || hasCustom) && (
        <div className="qo-modal-attrs">
          {/* Color */}
          {hasColors && (
            <div className={`qo-attr-group ${missingAttributes.color ? 'attr-error' : ''}`}>
              <span className="qo-attr-label">
                {currentLang === 'bn' ? 'রং' : 'Color'}
                {selectedColor ? (
                  <span className="qo-attr-selected"> — {selectedColor}</span>
                ) : missingAttributes.color ? (
                  <span className="attr-required-badge" style={{ marginLeft: 'auto', fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>
                    {currentLang === 'bn' ? '* আবশ্যক' : '* Required'}
                  </span>
                ) : null}
              </span>
              <div className="qo-color-swatches">
                {normalizeOptions(product.attributes!.colors!.options).map(opt => (
                  <button
                    key={opt.name}
                    type="button"
                    title={opt.name}
                    className={`qo-color-swatch ${selectedColor === opt.name ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedColor(prev => prev === opt.name ? '' : opt.name);
                      setMissingAttributes(prev => ({ ...prev, color: false }));
                    }}
                  >
                    <span className="qo-color-dot" style={{ background: opt.name.toLowerCase() }} />
                    <span className="qo-color-name">
                      {opt.name}
                      {opt.extra_price > 0 && (
                        <span className="attr-extra-badge">
                          {formatExtraPriceBadge(opt.extra_price, currentLang)}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          {hasSizes && (
            <div className={`qo-attr-group ${missingAttributes.size ? 'attr-error' : ''}`}>
              <span className="qo-attr-label">
                {currentLang === 'bn' ? 'সাইজ' : 'Size'}
                {selectedSize ? (
                  <span className="qo-attr-selected"> — {selectedSize}</span>
                ) : missingAttributes.size ? (
                  <span className="attr-required-badge" style={{ marginLeft: 'auto', fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>
                    {currentLang === 'bn' ? '* আবশ্যক' : '* Required'}
                  </span>
                ) : null}
              </span>
              <div className="qo-chips-row">
                {normalizeOptions(product.attributes!.sizes!.options).map(opt => (
                  <button
                    key={opt.name}
                    type="button"
                    className={`qo-chip ${selectedSize === opt.name ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedSize(prev => prev === opt.name ? '' : opt.name);
                      setMissingAttributes(prev => ({ ...prev, size: false }));
                    }}
                  >
                    {opt.name}
                    {opt.extra_price > 0 && (
                      <span className="attr-extra-badge">
                        {formatExtraPriceBadge(opt.extra_price, currentLang)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Weight */}
          {hasWeights && (
            <div className={`qo-attr-group ${missingAttributes.weight ? 'attr-error' : ''}`}>
              <span className="qo-attr-label">
                {currentLang === 'bn' ? 'ওজন' : 'Weight'}
                {selectedWeight ? (
                  <span className="qo-attr-selected"> — {selectedWeight}</span>
                ) : missingAttributes.weight ? (
                  <span className="attr-required-badge" style={{ marginLeft: 'auto', fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>
                    {currentLang === 'bn' ? '* আবশ্যক' : '* Required'}
                  </span>
                ) : null}
              </span>
              <div className="qo-chips-row">
                {normalizeOptions(product.attributes!.weights!.options).map(opt => (
                  <button
                    key={opt.name}
                    type="button"
                    className={`qo-chip ${selectedWeight === opt.name ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedWeight(prev => prev === opt.name ? '' : opt.name);
                      setMissingAttributes(prev => ({ ...prev, weight: false }));
                    }}
                  >
                    {opt.name}
                    {opt.extra_price > 0 && (
                      <span className="attr-extra-badge">
                        {formatExtraPriceBadge(opt.extra_price, currentLang)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Custom Attributes (e.g. Hijab with/without) — below_price first */}
          {[...(product.attributes?.custom || [])]
            .sort((a, b) => (b.placement === 'below_price' ? 1 : 0) - (a.placement === 'below_price' ? 1 : 0))
            .map(cg => {
            if (!cg.enabled || !cg.options || cg.options.length === 0) return null;
            const groupKey = cg.id || cg.name;
            const selectedVal = selectedCustom[groupKey] || '';
            const isMissing = missingAttributes[groupKey];
            const groupTitle = currentLang === 'bn' ? (cg.name_bn || cg.name) : (cg.name_en || cg.name);
            const options = normalizeOptions(cg.options);

            return (
              <div key={cg.id} className={`qo-attr-group ${isMissing ? 'attr-error' : ''}`}>
                <span className="qo-attr-label">
                  {groupTitle}
                  {selectedVal ? (
                    <span className="qo-attr-selected"> — {selectedVal}</span>
                  ) : isMissing ? (
                    <span className="attr-required-badge" style={{ marginLeft: 'auto', fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>
                      {currentLang === 'bn' ? '* আবশ্যক' : '* Required'}
                    </span>
                  ) : null}
                </span>
                <div className="qo-chips-row">
                  {options.map(opt => (
                    <button
                      key={opt.name}
                      type="button"
                      className={`qo-chip ${selectedVal === opt.name ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedCustom(prev => ({
                          ...prev,
                          [groupKey]: prev[groupKey] === opt.name ? '' : opt.name
                        }));
                        setMissingAttributes(prev => ({ ...prev, [groupKey]: false }));
                      }}
                    >
                      {opt.name}
                      {opt.extra_price > 0 && (
                        <span className="attr-extra-badge">
                          {formatExtraPriceBadge(opt.extra_price, currentLang)}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Quantity selector inside modal */}
          <div className="qo-quantity-row">
            <span className="qo-quantity-label">{currentLang === 'bn' ? 'পরিমাণ' : 'Quantity'}</span>
            <div className="qo-quantity-controls">
              <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
          </div>

        </div>
        )}

        {/* Confirm button — always pinned at bottom */}
        <div className="qo-confirm-area">
          <button className="qo-confirm-btn" onClick={handleConfirm}>
            {currentLang === 'bn' ? 'অর্ডার করুন' : 'Place Order'}
          </button>
        </div>

      </div>
    </div>
  );

  // Portal: render outside the card DOM to avoid stacking-context issues
  return ReactDOM.createPortal(modal, document.body);
};

export default QuickOrderModal;
